import { X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ACCOUNT_FILTER_LABELS,
  ALL_FILTER_VALUE,
  type AccountFilterKey,
  type AccountFilterOptions,
  type AccountFilters,
  type ActiveFilterChip,
} from "@/lib/accountListView";

// Derived, not hardcoded: a filter added to ACCOUNT_FILTER_LABELS without also
// updating a separate order list would silently never render a <select>.
const FILTER_ORDER = Object.keys(ACCOUNT_FILTER_LABELS) as AccountFilterKey[];

interface AccountsFilterBarProps {
  filters: AccountFilters;
  options: AccountFilterOptions;
  chips: ActiveFilterChip[];
  sortDescription: string | null;
  onFilterChange: (key: AccountFilterKey, value: string) => void;
  onClearAll: () => void;
}

export const AccountsFilterBar = ({
  filters,
  options,
  chips,
  sortDescription,
  onFilterChange,
  onClearAll,
}: AccountsFilterBarProps) => {
  const hasAppliedView = chips.length > 0 || sortDescription !== null;

  return (
    <div className="space-y-3 mb-4">
      <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {FILTER_ORDER.map((key) => (
          <label key={key} className="text-sm text-gray-600">
            <span className="block mb-1">{ACCOUNT_FILTER_LABELS[key]}</span>
            {/* Matches the district picker's native select on the Seat pools tab,
                and the search Input beside this bar — all h-10. */}
            <select
              className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              value={filters[key] === "" ? ALL_FILTER_VALUE : filters[key]}
              onChange={(event) =>
                onFilterChange(
                  key,
                  event.target.value === ALL_FILTER_VALUE ? "" : event.target.value
                )
              }
            >
              <option value={ALL_FILTER_VALUE}>All</option>
              {options[key].map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>

      {hasAppliedView ? (
        <div className="flex flex-wrap items-center gap-2">
          {chips.length > 0 ? (
            <>
              <span className="text-sm text-gray-600">Filters applied:</span>
              {chips.map((chip) => (
                <Badge key={chip.key} variant="secondary" className="gap-1 pr-1 font-normal">
                  {`${chip.label}: ${chip.value}`}
                  <button
                    type="button"
                    aria-label={`Remove ${chip.label} filter`}
                    onClick={() => onFilterChange(chip.key, "")}
                    className="rounded-full p-0.5 ring-offset-background transition-colors hover:bg-secondary-foreground/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <X className="h-3 w-3" aria-hidden />
                  </button>
                </Badge>
              ))}
            </>
          ) : null}

          {/* Same variant as the filter chips on purpose: they answer one question —
              what am I looking at. The "Sorted by:" prefix and the missing remove
              button are what set this one apart. */}
          {sortDescription ? (
            <Badge variant="secondary" className="font-normal">
              {`Sorted by: ${sortDescription}`}
            </Badge>
          ) : null}

          <Button type="button" variant="outline" size="sm" onClick={onClearAll}>
            Clear all
          </Button>
        </div>
      ) : null}
    </div>
  );
};
